import Axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { storage } from "../../utils";
import styles from "./SupplierPrices.module.css";

const FILTERS = [
  ["all", "همه"],
  ["ready", "قطعی"],
  ["needs_review", "نامطمئن"],
  ["unmatched", "بدون تطبیق"],
  ["invalid", "نامعتبر"],
  ["non_price", "بدون قیمت"],
];

const REASONS = {
  unpriced_to_price: "قیمت فعلی سایت خالی است",
  size_order: "ترتیب اندازه با سایت یکی نیست",
  multiple_matches: "چند تطبیق ممکن است",
  large_change: "تغییر زیاد است",
  extreme_change: "تغییر خیلی بزرگ است",
  no_verified_match: "تطبیق تأییدشده پیدا نشد",
  non_price: "این سلول قیمت نیست",
  conflicting_duplicate_prices: "برای یک کالا دو قیمت متفاوت آمده",
  no_price_column: "ستون قیمت پیدا نشد",
  zero_price: "مبلغ صفر پذیرفته نیست",
  tax_ambiguous: "وضعیت مالیات مشخص نیست",
  tax_inclusive_source: "مبلغ همان عدد لیست است (سند مالیات را ذکر کرده؛ کسر نشد)",
  identity_insufficient: "نام کالا برای تطبیق قطعی کافی نیست",
  currency_ambiguous: "ریال یا تومان مشخص نیست",
  ocr_uncertain: "خواندن تصویر برای قیمت کافی نبود",
  ocr_review: "قیمت تصویر باید دستی دیده شود",
  ocr_column_ambiguous: "چند مبلغ در تصویر دیده شد",
  ocr_warning_unresolved: "هشدار OCR حل نشده؛ ابتدا رقم/کد را اصلاح کنید",
  ocr_identity_required: "برای OCR باید کالای سایت را تأیید کنید",
  ocr_price_required: "برای OCR باید قیمت پیشنهادی را تأیید کنید",
  vision_review: "قیمت از vision خوانده شده و باید دستی تأیید شود",
  vision_mock_review: "نتیجهٔ شبیه‌سازی vision است؛ قیمت واقعی نیست",
  unit_mismatch: "واحد پول مبهم است",
};

const ERRORS = {
  ocr_unreliable: "از این تصویر قیمت قابل‌اتکا خوانده نشد",
  ocr_unavailable: "خواندن تصویر در دسترس نیست",
  ocr_unreadable: "تصویر باز نشد",
  ocr_review: "نتیجهٔ تصویر فقط برای بازبینی است",
  vision_disabled: "درک تصویری سند (vision) هنوز فعال نیست",
  vision_unavailable: "درک تصویری سند در دسترس نیست",
  vision_transmit_blocked: "ارسال سند به ارائه‌دهندهٔ vision مجاز نشده است",
  vision_unconfigured: "کلید/تنظیمات vision روی سرور نیست",
  vision_mock_simulated: "نتیجهٔ شبیه‌سازی است؛ قیمت واقعی تأمین‌کننده نیست",
  vision_pilot_limit: "سقف آزمایشی درخواست vision پر شد",
  xls_unreadable: "فایل اکسل قدیمی خوانده نشد",
  sheet_unreadable: "جدول خوانده نشد",
  unsupported_type: "نوع فایل پشتیبانی نمی‌شود",
  too_many_pages: "تعداد صفحه‌ها بیش از حد مجاز است",
  no_table: "جدول قیمت در این سند پیدا نشد یا ستون‌ها مبهم‌اند",
  pdf_extract_failed: "خواندن پی‌دی‌اف ناموفق بود",
  scanned_or_empty: "سند بدون متن قابل‌انتخاب است",
  column_ambiguous: "تشخیص ستون مبهم است؛ نام و قیمت را انتخاب کنید",
  too_large: "حجم یک فایل بیش از ۱۲ مگابایت است",
  batch_too_large: "مجموع حجم فایل‌ها بیش از ۶۴ مگابایت است",
  upload_rejected: "بارگذاری رد شد",
  timeout: "زمان پردازش تمام شد",
  processing_failed: "پردازش فایل ناتمام ماند",
  identity_insufficient: "نام کالا برای تطبیق قطعی کافی نیست",
  stale_pre_vat_rule: "این بازبینی با قاعدهٔ قدیمی سوپرپایپ است؛ دوباره استخراج کنید",
};

const LIMITS = {
  maxFiles: 8,
  maxFileBytes: 12 * 1024 * 1024,
  maxBatchBytes: 64 * 1024 * 1024,
};

const STATUS = {
  queued: "در صف",
  processing: "در حال استخراج",
  review: "در بازبینی",
  needs_layout: "نیاز به نگاشت ستون",
  failed: "ناموفق",
  applied: "اعمال‌شده",
  rolled_back: "بازگشت‌شده",
};

function money(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("fa-IR");
}

function bytes(n) {
  const v = Number(n) || 0;
  if (v < 1024) return `${v.toLocaleString("fa-IR")} بایت`;
  if (v < 1048576) return `${(v / 1024).toLocaleString("fa-IR", { maximumFractionDigits: 1 })} کیلوبایت`;
  return `${(v / 1048576).toLocaleString("fa-IR", { maximumFractionDigits: 1 })} مگابایت`;
}

function kind(name) {
  const ext = String(name || "").toLowerCase();
  if (ext.endsWith(".pdf")) return "پی‌دی‌اف";
  if (/\.(png|jpe?g|webp|heic|heif)$/.test(ext)) return "تصویر";
  if (ext.endsWith(".csv")) return "جدول متنی";
  if (/\.xlsx?$/.test(ext)) return "اکسل";
  return "فایل";
}

function when(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "تاریخ نامشخص";
  return d.toLocaleString("fa-IR");
}

function brandOf(row) {
  if (row.supplier === "superpipe") return "سوپرپایپ";
  if (row.supplier === "azin") return "آذین";
  if (row.supplier === "newpipe") return "نیوپایپ";
  return "نامشخص";
}

function uploadErrorMessage(error) {
  const status = error && error.response && error.response.status;
  const data = (error && error.response && error.response.data) || {};
  const ref = data.ref ? ` (کد پیگیری: ${data.ref})` : "";
  if (data.message) return `${data.message}${ref}`;
  if (status === 413) return `حجم فایل بیش از حد مجاز پروکسی است.${ref}`;
  if (status === 422) return `${ERRORS[data.code] || "استخراج قیمت ناموفق بود."}${ref}`;
  if (status === 400 && (!data || !Object.keys(data).length)) {
    return `بارگذاری ناقص یا رد شد (پاسخ خالی از سرور). دوباره بفرستید؛ اگر تکرار شد کد پیگیری را اعلام کنید.${ref}`;
  }
  if (status === 408 || (error && error.code === "ECONNABORTED")) {
    return `درخواست قطع شد. اگر استخراج در پس‌زمینه شروع شده، از تاریخچه همان دسته را باز کنید؛ دوباره نفرستید تا OCR تکراری نشود.${ref}`;
  }
  if (!error.response) return "اتصال به سرور برقرار نشد. شبکه را بررسی کنید.";
  return `${ERRORS[data.code] || "بارگذاری انجام نشد."}${ref}`;
}

function reasonOf(row) {
  if (row.reasons && row.reasons[0] && /[\u0600-\u06FF]/.test(row.reasons[0])) return row.reasons[0];
  return REASONS[row.reason] || "نیاز به بررسی";
}

async function token() {
  const value = await storage.load("token");
  if (!value) throw new Error("نشست پیدا نشد. دوباره وارد شوید.");
  return value;
}

const SupplierPrices = () => {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [over, setOver] = useState(false);
  const [batch, setBatch] = useState(null);
  const [excluded, setExcluded] = useState({});
  const [manual, setManual] = useState({});
  const [picks, setPicks] = useState({});
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(null);
  const [layoutPick, setLayoutPick] = useState({ nameIdx: "", sizeIdx: "", priceIdx: "", codeIdx: "" });
  const [edits, setEdits] = useState({});
  const [priceOk, setPriceOk] = useState({});
  const [identityOk, setIdentityOk] = useState({});

  const isOcr = (row) => Boolean(row && (row.ocr || row.tableOcr || row.layout === "avalai_ocr"));
  const editOf = (index, row) => edits[index] || {
    code: row.code || "",
    name: row.name || "",
    size: row.size || "",
    rawPrice: row.rawPrice || row.rawVatPrice || "",
    proposedToman: row.proposedToman != null ? String(row.proposedToman) : "",
  };
  const setEdit = (index, patch) => {
    setEdits((prev) => ({ ...prev, [index]: { ...editOf(index, rows[index] || {}), ...patch } }));
  };
  const evidenceUrl = (row) => {
    if (!batch || !row || !row.evidence || !row.evidence.cropPreview || !row.evidence.evidenceToken) return null;
    return `/supplier/batches/${batch.id}/evidence/${row.evidence.evidenceToken}/${row.evidence.cropPreview}`;
  };

  const loadHistory = async () => {
    const auth = await token();
    const res = await Axios.get("/supplier/batches", { headers: { "x-auth": auth } });
    setHistory(res.data.data || []);
  };

  useEffect(() => {
    loadHistory().catch(() => setHistory([]));
  }, []);

  const rows = batch ? batch.rows || [] : [];
  const counts = useMemo(() => {
    const out = { all: rows.length, ready: 0, needs_review: 0, unmatched: 0, invalid: 0, non_price: 0, duplicate: 0 };
    rows.forEach((row) => {
      out[row.group] = (out[row.group] || 0) + 1;
      if (row.reason === "conflicting_duplicate_prices") out.duplicate += 1;
    });
    return out;
  }, [rows]);

  const visible = rows.filter((row) => {
    if (filter !== "all" && row.group !== filter) return false;
    if (!query.trim()) return true;
    const hay = `${row.name || ""} ${row.code || ""} ${row.product ? row.product.title : ""}`;
    return hay.includes(query.trim());
  });

  const take = (list) => {
    const next = Array.from(list || []).slice(0, LIMITS.maxFiles);
    setFiles(next);
    setErr("");
    const total = next.reduce((sum, f) => sum + (f.size || 0), 0);
    if (next.some((f) => f.size > LIMITS.maxFileBytes)) {
      setErr(ERRORS.too_large);
    } else if (total > LIMITS.maxBatchBytes) {
      setErr(ERRORS.batch_too_large);
    }
  };

  const applyBatchPayload = (data, id) => {
    const preview = data.columnPreview || null;
    setBatch({
      id: id || data.id,
      previewHash: data.previewHash,
      rows: data.rows || [],
      errors: data.errors || data.extractErrors || [],
      needsLayoutMapping: Boolean(data.needsLayoutMapping) || data.status === "needs_layout",
      columnPreview: preview,
      applyEnabled: data.applyEnabled !== false,
      status: data.status,
      progress: data.progress || null,
    });
    setLayoutPick({
      nameIdx: preview && preview.suggested && preview.suggested.nameIdx != null ? String(preview.suggested.nameIdx) : "",
      sizeIdx: preview && preview.suggested && preview.suggested.sizeIdx != null ? String(preview.suggested.sizeIdx) : "",
      priceIdx: preview && preview.suggested && preview.suggested.priceIdx != null ? String(preview.suggested.priceIdx) : "",
      codeIdx: preview && preview.suggested && preview.suggested.codeIdx != null ? String(preview.suggested.codeIdx) : "",
    });
    setExcluded({});
    setManual({});
    setPicks({});
    setEdits({});
    setPriceOk({});
    setIdentityOk({});
  };

  const pollJob = async (auth, jobId) => {
    const started = Date.now();
    const maxWait = 15 * 60 * 1000;
    while (Date.now() - started < maxWait) {
      const prog = await Axios.get(`/supplier/batches/${jobId}/progress`, {
        headers: { "x-auth": auth },
        timeout: 30000,
      });
      const p = prog.data || {};
      setMsg(p.message || (p.progress && p.progress.detail) || "در حال استخراج…");
      setBatch((prev) => ({
        ...(prev || {}),
        id: jobId,
        status: p.status,
        progress: p.progress || null,
        applyEnabled: false,
        rows: (prev && prev.rows) || [],
      }));
      if (p.done) {
        if (p.status === "failed") {
          const problems = (p.errors || []).map((item) => item.detail || ERRORS[item.code] || "استخراج ناموفق بود");
          setErr((p.message || problems.join(" ") || "استخراج قیمت ناموفق بود.") + (p.ref ? ` (کد پیگیری: ${p.ref})` : ""));
          setMsg("");
          const full = await Axios.get(`/supplier/batches/${jobId}`, { headers: { "x-auth": auth }, timeout: 60000 });
          applyBatchPayload(full.data, jobId);
          return;
        }
        const full = await Axios.get(`/supplier/batches/${jobId}`, { headers: { "x-auth": auth }, timeout: 60000 });
        applyBatchPayload(full.data, jobId);
        if (full.data.needsLayoutMapping || full.data.status === "needs_layout") {
          setErr(full.data.message || ERRORS.column_ambiguous || "تشخیص ستون مبهم است.");
          setMsg("");
        } else if (full.data.extractionOk === false) {
          const problems = (full.data.extractErrors || []).map((item) => item.detail || ERRORS[item.code] || "استخراج ناموفق بود");
          setErr((full.data.message || problems.join(" ") || "استخراج قیمت ناموفق بود.") + (full.data.ref ? ` (کد پیگیری: ${full.data.ref})` : ""));
          setMsg("");
        } else {
          setMsg(full.data.message || "استخراج و تطبیق انجام شد. نتیجه را بازبینی کنید.");
        }
        return;
      }
      await new Promise((r) => setTimeout(r, Math.max(800, Number(p.pollAfterMs) || 1200)));
    }
    setErr("استخراج هنوز تمام نشده است. از تاریخچه همین دسته را باز کنید؛ دوباره آپلود نکنید.");
  };

  const upload = async () => {
    if (!files.length) {
      setErr("ابتدا یک فایل انتخاب کنید.");
      return;
    }
    const total = files.reduce((sum, f) => sum + (f.size || 0), 0);
    if (files.length > LIMITS.maxFiles) {
      setErr(`حداکثر ${LIMITS.maxFiles} فایل مجاز است.`);
      return;
    }
    if (files.some((f) => f.size > LIMITS.maxFileBytes)) {
      setErr(ERRORS.too_large);
      return;
    }
    if (total > LIMITS.maxBatchBytes) {
      setErr(ERRORS.batch_too_large);
      return;
    }
    setBusy(true);
    setErr("");
    setMsg("در حال ارسال فایل…");
    try {
      const auth = await token();
      const body = new FormData();
      files.forEach((file) => body.append("file", file));
      // Upload only — extraction runs as a background job (no long HTTP wait).
      const res = await Axios.post("/supplier/batches", body, {
        headers: { "x-auth": auth },
        timeout: 120000,
        maxContentLength: LIMITS.maxBatchBytes + 8 * 1024 * 1024,
        maxBodyLength: LIMITS.maxBatchBytes + 8 * 1024 * 1024,
        validateStatus: (s) => (s >= 200 && s < 300) || s === 202 || s === 422,
      });
      const jobId = res.data.jobId || res.data.id;
      if (!jobId) throw new Error("job_id_missing");
      if (res.data.deduped) {
        setMsg(res.data.message || "نتیجهٔ قبلی همین فایل بازیابی شد.");
      }
      if (res.data.done && (res.data.rows || []).length) {
        applyBatchPayload(res.data, jobId);
        setMsg(res.data.message || "استخراج و تطبیق انجام شد. نتیجه را بازبینی کنید.");
      } else {
        setBatch({
          id: jobId,
          previewHash: null,
          rows: [],
          errors: [],
          status: res.data.status || "queued",
          progress: res.data.progress || null,
          applyEnabled: false,
        });
        setMsg(res.data.message || "فایل دریافت شد؛ در حال استخراج…");
        await pollJob(auth, jobId);
      }
      await loadHistory();
    } catch (error) {
      setErr(uploadErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const openBatch = async (id) => {
    setBusy(true);
    setErr("");
    try {
      const auth = await token();
      const res = await Axios.get(`/supplier/batches/${id}`, { headers: { "x-auth": auth } });
      if (res.data.status === "queued" || res.data.status === "processing") {
        setBatch({
          id,
          previewHash: null,
          rows: [],
          errors: [],
          status: res.data.status,
          progress: res.data.progress || null,
          applyEnabled: false,
        });
        setMsg(res.data.message || "استخراج هنوز در جریان است…");
        await pollJob(auth, id);
        await loadHistory();
        return;
      }
      applyBatchPayload(res.data, id);
      setBatch((prev) => ({
        ...(prev || {}),
        reextractRequired: Boolean(res.data.reextractRequired),
        ruleNotice: res.data.ruleNotice || "",
      }));
      if (res.data.reextractRequired) {
        setErr(res.data.ruleNotice || ERRORS.stale_pre_vat_rule);
        setMsg("");
      } else {
        setMsg(res.data.message || "دستهٔ ذخیره‌شده باز شد.");
      }
    } catch (error) {
      setErr("جزئیات این دسته خوانده نشد.");
    } finally {
      setBusy(false);
    }
  };

  const impact = useMemo(() => {
    let toman = 0;
    let count = 0;
    rows.forEach((row) => {
      const index = Number.isInteger(row.index) ? row.index : rows.indexOf(row);
      if (excluded[index]) return;
      const selected = row.group === "ready" || manual[index] || picks[index];
      if (!selected || row.previousToman == null || row.proposedToman == null) return;
      toman += Number(row.proposedToman) - Number(row.previousToman);
      count += 1;
    });
    return { toman, count };
  }, [rows, excluded, manual, picks]);

  const selectSafe = () => {
    const next = {};
    rows.forEach((row) => {
      const index = Number.isInteger(row.index) ? row.index : rows.indexOf(row);
      if (row.group !== "ready") next[index] = true;
    });
    setExcluded(next);
    setManual({});
    setPicks({});
  };

  const applyLayout = async () => {
    if (!batch || !batch.id) return;
    if (layoutPick.nameIdx === "" || layoutPick.priceIdx === "") {
      setErr("ستون نام کالا و ستون قیمت را انتخاب کنید.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const auth = await token();
      const res = await Axios.post(`/supplier/batches/${batch.id}/layout`, {
        nameIdx: Number(layoutPick.nameIdx),
        sizeIdx: layoutPick.sizeIdx === "" ? null : Number(layoutPick.sizeIdx),
        priceIdx: Number(layoutPick.priceIdx),
        codeIdx: layoutPick.codeIdx === "" ? null : Number(layoutPick.codeIdx),
      }, { headers: { "x-auth": auth } });
      setBatch({
        id: res.data.id,
        previewHash: res.data.previewHash,
        rows: res.data.rows || [],
        errors: [],
        needsLayoutMapping: false,
        columnPreview: null,
        applyEnabled: true,
      });
      setMsg(res.data.message || "با نگاشت ستون دوباره پردازش شد.");
    } catch (error) {
      const text = error.response && error.response.data && error.response.data.message;
      setErr(text || "اعمال نگاشت ستون انجام نشد.");
    } finally {
      setBusy(false);
    }
  };

  const confirmApply = async () => {
    if (!batch || !batch.previewHash) return;
    if (batch.reextractRequired || batch.applyEnabled === false || batch.needsLayoutMapping) {
      setErr(batch.needsLayoutMapping ? ERRORS.column_ambiguous : (batch.ruleNotice || ERRORS.stale_pre_vat_rule));
      return;
    }
    const unresolved = rows.filter((row, index) => {
      const i = Number.isInteger(row.index) ? row.index : index;
      if (!isOcr(row) || excluded[i] || !manual[i]) return false;
      const warning = row.ocrWarning || row.reviewReason === "ocr_uncertain";
      const edited = Boolean(edits[i]);
      if (warning && !edited) return true;
      if (!priceOk[i]) return true;
      if (!(picks[i] || (row.product && identityOk[i]))) return true;
      return false;
    });
    if (unresolved.length) {
      setErr(`تأیید عمومی ردیف‌های OCR حل‌نشده را دور نمی‌زند. ${unresolved.length.toLocaleString("fa-IR")} ردیف هنوز نیاز به اصلاح/تأیید هویت و قیمت دارد.`);
      return;
    }
    if (!window.confirm("فقط ردیف‌های قطعی و ردیف‌های نامطمئنی که هویت کالا و قیمت‌شان دستی تأیید شده روی قیمت پایه نوشته شوند؟ تخفیف کالا، برند و سفارش‌ها عوض نمی‌شوند.")) return;
    setBusy(true);
    setErr("");
    try {
      const auth = await token();
      const decisions = rows.map((row, index) => {
        const i = Number.isInteger(row.index) ? row.index : index;
        const ed = edits[i];
        const ocr = isOcr(row);
        const approved = ocr
          ? Boolean(manual[i] && priceOk[i] && (picks[i] || (row.product && identityOk[i])))
          : Boolean(manual[i] || picks[i]);
        return {
          index: i,
          excluded: Boolean(excluded[i]),
          manualApproved: approved,
          productId: picks[i] || undefined,
          identityConfirmed: Boolean(identityOk[i] || picks[i]),
          priceConfirmed: Boolean(priceOk[i]),
          code: ed ? ed.code : undefined,
          name: ed ? ed.name : undefined,
          size: ed ? ed.size : undefined,
          rawPrice: ed ? ed.rawPrice : undefined,
          proposedToman: ed && ed.proposedToman !== "" ? Number(ed.proposedToman) : undefined,
        };
      }).filter((item) => item.excluded || item.manualApproved || item.productId || item.code || item.rawPrice);
      const res = await Axios.post(`/supplier/batches/${batch.id}/confirm`, {
        confirm: true,
        previewHash: batch.previewHash,
        decisions,
      }, { headers: { "x-auth": auth } });
      const applied = Number(res.data.applied || 0);
      const skipped = Number(res.data.skipped || 0);
      setMsg(`اعمال انجام شد. ${applied.toLocaleString("fa-IR")} قیمت پایه نوشته شد و ${skipped.toLocaleString("fa-IR")} ردیف رد شد.`);
      await loadHistory();
    } catch (error) {
      const text = error.response && error.response.data && error.response.data.message;
      setErr(text || "اعمال قیمت انجام نشد.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={styles.root}>
      <header className={styles.head}>
        <h1>به‌روزرسانی قیمت از لیست</h1>
        <p>
          تطبیق فقط داخل برند تأییدشده انجام می‌شود. استخراج جدول/متن، اکسل و OCR جدول فعال است.
          برای ردیف‌های OCR باید برش منبع، فیلدهای استخراج‌شده و کالای سایت را ببینید؛ تأیید عمومی هشدار OCR را دور نمی‌زند.
          قیمت فقط بعد از تأیید صریح شما نوشته می‌شود.
        </p>
      </header>
      <div className={styles.steps}>
        <span className={files.length ? styles.stepOn : styles.step}>۱ بارگذاری</span>
        <span className={busy ? styles.stepOn : styles.step}>۲ استخراج</span>
        <span className={busy ? styles.stepOn : styles.step}>۳ تطبیق</span>
        <span className={batch ? styles.stepOn : styles.step}>۴ بازبینی</span>
        <span className={batch ? styles.stepOn : styles.step}>۵ تأیید</span>
        <span className={styles.step}>۶ تاریخچه</span>
      </div>

      <section className={styles.card}>
        <div
          className={over ? `${styles.drop} ${styles.dropOn}` : styles.drop}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files); }}
        >
          <strong>فایل را اینجا رها کنید</strong>
          <span>پی‌دی‌اف، تصویر، اکسل یا جدول متنی. حداکثر هشت فایل.</span>
          <button type="button" className={styles.pick} disabled={busy} onClick={() => inputRef.current && inputRef.current.click()}>
            انتخاب فایل
          </button>
          <input
            ref={inputRef}
            hidden
            type="file"
            multiple
            accept=".pdf,.csv,.xlsx,.xls,image/jpeg,image/png,image/webp,image/heic,image/heif"
            onChange={(e) => take(e.target.files)}
          />
        </div>
        {files.length ? (
          <div className={styles.fileList}>
            {files.map((file) => (
              <div className={styles.file} key={file.name + file.size}>
                <span>{file.name}</span>
                <span>{kind(file.name)} · {bytes(file.size)} · آمادهٔ ارسال</span>
              </div>
            ))}
          </div>
        ) : null}
        {busy ? <div className={styles.bar} role="progressbar" aria-label="در حال پردازش" /> : null}
        <div className={styles.tools}>
          <button type="button" className={styles.go} disabled={busy || !files.length} onClick={upload}>
            {busy ? "در حال استخراج و تطبیق" : "شروع استخراج و تطبیق"}
          </button>
        </div>
        {err ? <div className={styles.err} role="alert">{err}</div> : null}
        {msg ? <div className={styles.note}>{msg}</div> : null}
      </section>

      {batch && batch.needsLayoutMapping && batch.columnPreview ? (
        <section className={styles.card}>
          <h2>انتخاب ستون‌ها</h2>
          <p>{batch.columnPreview.message || ERRORS.column_ambiguous}</p>
          <div className={styles.fileList}>
            {(batch.columnPreview.headers || []).map((h, i) => (
              <div className={styles.file} key={`h-${i}`}><span>{i}</span><span>{h || "—"}</span></div>
            ))}
          </div>
          <div className={styles.actions}>
            <label>نام کالا
              <select value={layoutPick.nameIdx} onChange={(e) => setLayoutPick((p) => ({ ...p, nameIdx: e.target.value }))}>
                <option value="">انتخاب</option>
                {(batch.columnPreview.headers || []).map((h, i) => <option key={`n-${i}`} value={i}>{i}: {h || "خالی"}</option>)}
              </select>
            </label>
            <label>اندازه
              <select value={layoutPick.sizeIdx} onChange={(e) => setLayoutPick((p) => ({ ...p, sizeIdx: e.target.value }))}>
                <option value="">اختیاری</option>
                {(batch.columnPreview.headers || []).map((h, i) => <option key={`s-${i}`} value={i}>{i}: {h || "خالی"}</option>)}
              </select>
            </label>
            <label>قیمت
              <select value={layoutPick.priceIdx} onChange={(e) => setLayoutPick((p) => ({ ...p, priceIdx: e.target.value }))}>
                <option value="">انتخاب</option>
                {(batch.columnPreview.headers || []).map((h, i) => <option key={`p-${i}`} value={i}>{i}: {h || "خالی"}</option>)}
              </select>
            </label>
            <label>کد
              <select value={layoutPick.codeIdx} onChange={(e) => setLayoutPick((p) => ({ ...p, codeIdx: e.target.value }))}>
                <option value="">اختیاری</option>
                {(batch.columnPreview.headers || []).map((h, i) => <option key={`c-${i}`} value={i}>{i}: {h || "خالی"}</option>)}
              </select>
            </label>
            <button type="button" className={styles.go} disabled={busy} onClick={applyLayout}>اعمال نگاشت و استخراج مجدد</button>
          </div>
        </section>
      ) : null}

      {batch ? (
        <section className={styles.card}>
          <div className={styles.cards}>
            <div className={styles.stat}><b>{counts.all.toLocaleString("fa-IR")}</b><span>کل ردیف‌ها</span></div>
            <div className={styles.stat}><b>{(counts.ready || 0).toLocaleString("fa-IR")}</b><span>قطعی</span></div>
            <div className={styles.stat}><b>{(counts.needs_review || 0).toLocaleString("fa-IR")}</b><span>نامطمئن</span></div>
            <div className={styles.stat}><b>{(counts.unmatched || 0).toLocaleString("fa-IR")}</b><span>بدون تطبیق</span></div>
            <div className={styles.stat}><b>{(counts.invalid || 0).toLocaleString("fa-IR")}</b><span>نامعتبر</span></div>
            <div className={styles.stat}><b>{counts.duplicate.toLocaleString("fa-IR")}</b><span>تکراری متعارض</span></div>
          </div>
          <div className={styles.tools}>
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="جستجو در نام کالا، کد یا محصول سایت" />
            <button type="button" className={styles.chip} onClick={selectSafe}>فقط ردیف‌های قطعی</button>
            <span>اثر انتخاب فعلی: {impact.count.toLocaleString("fa-IR")} ردیف، اختلاف {money(impact.toman)} تومان</span>
            {FILTERS.map(([key, label]) => (
              <button key={key} type="button" className={filter === key ? `${styles.chip} ${styles.chipOn}` : styles.chip} onClick={() => setFilter(key)}>
                {label}
              </button>
            ))}
          </div>
          {visible.length === 0 ? <div className={styles.note}>ردیفی با این صافی پیدا نشد.</div> : (
            <div className={styles.tableWrap}>
              <table>
                <thead>
                  <tr>
                    <th>بازبینی / اعمال</th>
                    <th>شواهد منبع</th>
                    <th>استخراج (قابل ویرایش)</th>
                    <th>برند</th>
                    <th>کالای پیشنهادی همان برند</th>
                    <th>قیمت قبلی</th>
                    <th>پیشنهاد (تومان)</th>
                    <th>اختلاف</th>
                    <th>دلیل</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.slice(0, 80).map((row) => {
                    const index = Number.isInteger(row.index) ? row.index : rows.indexOf(row);
                    const ed = editOf(index, row);
                    const crop = evidenceUrl(row);
                    const ocr = isOcr(row);
                    return (
                    <tr key={`${row.code}-${row.line}-${index}`} className={row.group === "needs_review" || row.group === "invalid" || row.ocrWarning ? styles.warn : undefined}>
                      <td data-label="بازبینی / اعمال">
                        <input type="checkbox" checked={Boolean(excluded[index])} onChange={(event) => setExcluded((prev) => ({ ...prev, [index]: event.target.checked }))} aria-label="حذف این ردیف از اعمال" />
                        <span>حذف از اعمال</span>
                        {row.group === "needs_review" || ocr ? (
                          <label>
                            <input type="checkbox" checked={Boolean(manual[index])} onChange={(event) => setManual((prev) => ({ ...prev, [index]: event.target.checked }))} />
                            تأیید ردیف
                          </label>
                        ) : null}
                        {ocr ? (
                          <label>
                            <input type="checkbox" checked={Boolean(priceOk[index])} onChange={(event) => setPriceOk((prev) => ({ ...prev, [index]: event.target.checked }))} />
                            تأیید قیمت
                          </label>
                        ) : null}
                        {ocr && (row.product || (row.suggestions && row.suggestions.length)) ? (
                          <label>
                            <input type="checkbox" checked={Boolean(identityOk[index])} onChange={(event) => setIdentityOk((prev) => ({ ...prev, [index]: event.target.checked }))} />
                            تأیید هویت کالا
                          </label>
                        ) : null}
                        {row.productKind ? <div><small>{row.productKind === "fitting" ? "اتصال" : row.productKind === "pipe" ? "لوله" : "سایر"}</small></div> : null}
                      </td>
                      <td data-label="شواهد منبع">
                        {crop ? (
                          <img className={styles.crop} src={crop} alt="برش منبع ردیف" loading="lazy" />
                        ) : ocr ? (
                          <span className={styles.muted}>برش منبع در این ردیف ثبت نشده</span>
                        ) : (
                          <span className={styles.muted}>متن/جدول — بدون OCR</span>
                        )}
                      </td>
                      <td data-label="استخراج (قابل ویرایش)">
                        <div className={styles.editGrid}>
                          <label>کد
                            <input value={ed.code} onChange={(e) => setEdit(index, { code: e.target.value })} />
                          </label>
                          <label>نام
                            <input value={ed.name} onChange={(e) => setEdit(index, { name: e.target.value })} />
                          </label>
                          <label>اندازه
                            <input value={ed.size} onChange={(e) => setEdit(index, { size: e.target.value })} />
                          </label>
                          <label>مبلغ خام (ریال)
                            <input value={ed.rawPrice} onChange={(e) => setEdit(index, { rawPrice: e.target.value })} />
                          </label>
                        </div>
                        {row.ocrWarning ? <div className={styles.err}>هشدار OCR — قبل از اعمال اصلاح کنید</div> : null}
                      </td>
                      <td data-label="برند">{brandOf(row)}</td>
                      <td data-label="کالای پیشنهادی همان برند">
                        <div>{row.product ? row.product.title : "بدون تطبیق قطعی"}</div>
                        {row.suggestions && row.suggestions.length ? (
                          <select
                            value={picks[index] || ""}
                            onChange={(event) => {
                              setPicks((prev) => ({ ...prev, [index]: event.target.value }));
                              setIdentityOk((prev) => ({ ...prev, [index]: Boolean(event.target.value) }));
                            }}
                            aria-label="انتخاب کالای همان برند"
                          >
                            <option value="">انتخاب از کاندیداهای همان برند</option>
                            {row.suggestions.slice(0, 8).map((item) => (
                              <option key={item.id} value={item.id}>{item.title} · {item.size || "بدون اندازه"}</option>
                            ))}
                          </select>
                        ) : null}
                      </td>
                      <td data-label="قیمت قبلی">{money(row.previousToman)}</td>
                      <td data-label="پیشنهاد (تومان)">
                        {ocr ? (
                          <input
                            value={ed.proposedToman}
                            onChange={(e) => setEdit(index, { proposedToman: e.target.value })}
                            aria-label="قیمت پیشنهادی تومان"
                          />
                        ) : money(row.proposedToman)}
                      </td>
                      <td data-label="اختلاف">{row.previousToman == null || row.proposedToman == null ? "—" : money(Number(row.proposedToman) - Number(row.previousToman))}</td>
                      <td data-label="دلیل">{reasonOf(row)}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {visible.length > 80 ? <p>هشتاد ردیف اول این صافی نشان داده می‌شود.</p> : null}
          <div className={styles.lockbox}>
            قیمت فقط با تأیید شما نوشته می‌شود. ردیف OCR بدون برش/اصلاح هشدار، بدون تأیید هویت کالا و بدون تأیید قیمت اعمال نمی‌شود؛ تأیید عمومی این شرط‌ها را دور نمی‌زند.
            <div>
              <button
                type="button"
                className={styles.go}
                disabled={busy || batch.reextractRequired || batch.applyEnabled === false}
                onClick={confirmApply}
              >
                تأیید و نوشتن قیمت پایه
              </button>
            </div>
          </div>
        </section>
      ) : <div className={styles.note}>هنوز دسته‌ای برای بازبینی باز نشده است.</div>}

      <section className={styles.card} id="supplier-history">
        <h2>تاریخچه</h2>
        {history == null ? <p>در حال خواندن تاریخچه</p> : history.length === 0 ? <p>هنوز دسته‌ای ثبت نشده است.</p> : (
          <div className={styles.history}>
            {history.map((item) => (
              <button type="button" className={styles.histBtn} key={item._id} onClick={() => openBatch(item._id)}>
                <strong>{STATUS[item.status] || "وضعیت ثبت‌شده"}</strong>
                <div>{(item.files || []).map((file) => file.name).join("، ") || "بدون نام فایل"}</div>
                <small>{when(item.createdAt)} · {(item.files || []).map((file) => bytes(file.bytes)).join("، ")}</small>
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export { SupplierPrices };
